import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList,
} from "@/components/ui/command";

/**
 * UnitCombobox — replacement for the free-text Unit input on the
 * Candidate Review table. Canonical units come from EC via the
 * bulk-capture/units proxy; Bulk Capture never owns the unit master.
 *
 * Behaviour:
 *   - Renders a Popover Combobox with two groups: "Recently used"
 *     (frequency-ranked from the outlet's last ~50 candidates) and
 *     "All units" (canonical).
 *   - Search matches code, name and aliases. Case-insensitive.
 *   - If the current `value` is a legacy unit not in canonical, it
 *     stays displayed verbatim and is preserved on save.
 *   - When EC units endpoint is degraded (units=[]), the component
 *     falls back to a plain text Input with a one-line hint so the
 *     review screen never blocks on units.
 *   - Pressing Enter on the search input commits the typed value as
 *     a free-text unit (preserves the fast-operator workflow).
 *
 * Props:
 *   value         current unit code (string, possibly legacy)
 *   onChange(v)   commit handler — `v` is a string (code)
 *   disabled      pass-through (readonly mode)
 *   units         canonical units from /bulk-capture/units
 *   recentUnits   list of unit code strings, recent-first
 *   degraded      true if backend reported a degraded EC fetch
 *   testid        data-testid for the trigger / input
 */
export default function UnitCombobox({
  value, onChange, disabled, units, recentUnits, degraded, testid,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const canonicalByCode = useMemo(() => {
    const m = new Map();
    (units || []).forEach((u) => m.set(u.code.toLowerCase(), u));
    return m;
  }, [units]);

  // Recent codes that are still in canonical (full meta) AND those that
  // aren't (legacy) — both get shown, legacy items get a "(legacy)" tag.
  const recentBuckets = useMemo(() => {
    const seen = new Set();
    const out = [];
    (recentUnits || []).forEach((code) => {
      const lower = (code || "").toLowerCase();
      if (!lower || seen.has(lower)) return;
      seen.add(lower);
      out.push({
        code: lower,
        label: canonicalByCode.get(lower)?.label || lower,
        legacy: !canonicalByCode.has(lower),
      });
    });
    return out;
  }, [recentUnits, canonicalByCode]);

  // Active canonical units only, sorted alpha by code.
  const allUnits = useMemo(
    () =>
      (units || [])
        .filter((u) => u.is_active !== false)
        .slice()
        .sort((a, b) => a.code.localeCompare(b.code)),
    [units]
  );

  // Free-text fallback when EC is unavailable AND we have no cached units.
  // Keeps the operator unblocked; legacy values still round-trip.
  if (degraded && (!units || units.length === 0)) {
    return (
      <div className="flex flex-col gap-0.5">
        <Input
          data-testid={testid}
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="border-archival h-8"
        />
        <span className="text-[10px] text-neutral-500">
          Units list unavailable. Type manually.
        </span>
      </div>
    );
  }

  // Filter: match code / label / name / aliases against query.
  const q = query.trim().toLowerCase();
  const matches = (u) => {
    if (!q) return true;
    if (u.code.toLowerCase().includes(q)) return true;
    if ((u.label || "").toLowerCase().includes(q)) return true;
    if ((u.name || "").toLowerCase().includes(q)) return true;
    if ((u.aliases || []).some((a) => (a || "").toLowerCase().includes(q))) return true;
    return false;
  };
  const matchesRecent = (r) => {
    if (!q) return true;
    if (r.code.includes(q)) return true;
    if ((r.label || "").toLowerCase().includes(q)) return true;
    return false;
  };

  const commit = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  // Enter inside the search input — if no match selected via arrows,
  // commit the typed value as a free-text unit (fast-operator path).
  const onSearchKeyDown = (e) => {
    if (e.key === "Enter" && q) {
      // Defer to give cmdk a chance to pick a highlighted item first.
      // If `value` is unchanged 1 tick later, commit free-text.
      const typed = q;
      setTimeout(() => {
        // We can't detect cmdk selection from here; safest behavior:
        // if user explicitly hits Enter on a non-matching query, commit
        // it verbatim. If it matched, cmdk already handled selection.
        const matchedAny =
          allUnits.some(matches) || recentBuckets.some(matchesRecent);
        if (!matchedAny) commit(typed);
      }, 0);
    }
  };

  const displayLabel = value ? value : "Unit";
  const isLegacy =
    value && !canonicalByCode.has((value || "").toLowerCase());

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled}
          className="border-archival h-8 px-2 inline-flex items-center justify-between gap-1 text-sm bg-white w-full hover:bg-neutral-50 disabled:opacity-50 rounded-sm"
        >
          <span className={value ? "truncate" : "text-neutral-400"}>
            {displayLabel}
            {isLegacy && value ? (
              <span
                data-testid={testid ? `${testid}-legacy-tag` : undefined}
                className="ml-1 text-[9px] text-amber-700 uppercase tracking-wide">
                legacy
              </span>
            ) : null}
          </span>
          <ChevronDown size={14} className="opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0"
        align="start"
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder="Search unit…"
            value={query}
            onValueChange={setQuery}
            onKeyDown={onSearchKeyDown}
            data-testid={testid ? `${testid}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>
              {q ? (
                <button
                  type="button"
                  className="w-full text-left px-2 py-1 text-xs hover:bg-neutral-50"
                  onClick={() => commit(q)}
                  data-testid={testid ? `${testid}-commit-custom` : undefined}
                >
                  Use “{q}” as custom unit
                </button>
              ) : (
                <span className="px-2 py-1 text-xs text-neutral-500">No units</span>
              )}
            </CommandEmpty>

            {recentBuckets.length > 0 && (
              <CommandGroup heading="Recently used">
                {recentBuckets.filter(matchesRecent).map((r) => (
                  <CommandItem
                    key={`recent-${r.code}`}
                    value={`recent-${r.code}`}
                    onSelect={() => commit(r.code)}
                    data-testid={testid ? `${testid}-recent-${r.code}` : undefined}
                  >
                    <span className="font-mono">{r.code}</span>
                    {r.label !== r.code && (
                      <span className="ml-2 text-neutral-500 text-xs">{r.label}</span>
                    )}
                    {r.legacy && (
                      <span className="ml-auto text-[9px] text-amber-700 uppercase">legacy</span>
                    )}
                    {value === r.code && <Check size={12} className="ml-auto" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {allUnits.length > 0 && (
              <CommandGroup heading="All units">
                {allUnits.filter(matches).map((u) => (
                  <CommandItem
                    key={u.code}
                    value={u.code}
                    onSelect={() => commit(u.code)}
                    data-testid={testid ? `${testid}-opt-${u.code}` : undefined}
                  >
                    <span className="font-mono">{u.code}</span>
                    <span className="ml-2 text-neutral-500 text-xs">{u.label}</span>
                    {value === u.code && <Check size={12} className="ml-auto" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
